import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Container, Paper } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/styles";
import { NavBar } from "../shared/NavBar";
import { ErrorSnackbar } from "../shared/Error/ErrorSnackbar";
import { ErrorMessageSnackbar } from "../shared/ErrorMessageSnackbar";
import {
  defaultMaterialTheme,
  Header,
  LoadingBox,
} from "../shared/Style/Style";
import { useMutation, useQuery } from "@apollo/client";
import { convertDateAndTimeToLocaleHandler } from "../shared/Utils/ConvertOperations/convertDateAndTimeToLocaleHandler";
import {
  CreateApplicationVersionDocument,
  CreateApplicationVersionMutation,
  GetApplicationVersionsDocument,
  GetApplicationVersionsQuery,
  Os,
} from "../../generated/graphql";

import { navItems } from "../../constants/navItemMobileApp";
import { VersionFormBlock } from "../shared/Versions/VersionFormBlock";
import { VersionHistoryBlock } from "../shared/Versions/VersionHistoryBlock";

export const MobileVersions = () => {
  const { appId } = useParams<{ appId: string }>();

  const [mobileIOS, setMobileIOS] = useState<string>();
  const [mobileAndroid, setMobileAndroid] = useState<string>();

  const [requestError, setRequestError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { data, loading, error } = useQuery<GetApplicationVersionsQuery>(
    GetApplicationVersionsDocument,
    {
      variables: {
        appId,
      },
    }
  );

  const [
    createMobileVersion,
    {
      data: createMobileVersionData,
      loading: createMobileVersionLoading,
      error: createMobileVersionError,
    },
  ] = useMutation<CreateApplicationVersionMutation>(
    CreateApplicationVersionDocument,
    {
      variables: { appId },
      refetchQueries: [
        { query: GetApplicationVersionsDocument, variables: { appId } },
      ],
    }
  );

  const errorHandler = (message: string) => {
    setRequestError(true);
    setErrorMessage(message);
  };

  const createVersionHandle = async (type: Os) => {
    try {
      if (!mobileIOS && !mobileAndroid) {
        return;
      }

      await createMobileVersion({
        variables: {
          os: type,
          version: type === Os.IOs ? mobileIOS : mobileAndroid,
        },
      });
    } catch (e) {
      errorHandler("При збереженні версії виникла помилка");
    }
  };

  useEffect(() => {
    if (createMobileVersionData?.createApplicationVersion.os === Os.IOs) {
      setMobileIOS("");
    }
    if (createMobileVersionData?.createApplicationVersion.os === Os.Android) {
      setMobileAndroid("");
    }
  }, [createMobileVersionData]);

  const disableOnLoading = createMobileVersionLoading || loading;

  let content;

  if (disableOnLoading) {
    content = (
      <LoadingBox>
        <CircularProgress color="inherit" />
      </LoadingBox>
    );
  }

  if (error) {
    content = <ErrorSnackbar error={error} />;
  }

  if (data) {
    const versionsData = data.getApplicationVersions;

    const versionsIOS = versionsData.filter((version) => version.os === Os.IOs);
    const versionsAndroid = versionsData.filter(
      (version) => version.os === Os.Android
    );

    content = (
      <>
        <Box p="60px 0 120px 0">
          <Box display="flex" justifyContent="space-around">
            <VersionFormBlock
              title="Версія для IOS"
              value={mobileIOS}
              onChange={({ target: { value } }) => setMobileIOS(value)}
              onSave={() => createVersionHandle(Os.IOs)}
              disabled={disableOnLoading}
              lastVersion={versionsIOS[0]?.version || ""}
              lastDate={
                convertDateAndTimeToLocaleHandler(
                  versionsIOS[0]?.createdDate
                ) || ""
              }
            />
            <VersionFormBlock
              title="Версія для Android"
              value={mobileAndroid}
              onChange={({ target: { value } }) => setMobileAndroid(value)}
              onSave={() => createVersionHandle(Os.Android)}
              disabled={disableOnLoading}
              lastVersion={versionsAndroid[0]?.version || ""}
              lastDate={
                convertDateAndTimeToLocaleHandler(
                  versionsAndroid[0]?.createdDate
                ) || ""
              }
            />
          </Box>
          <Box display="flex" justifyContent="center">
            <h2>Історія оновлення застосунку </h2>
          </Box>
          <Box display="flex" justifyContent="space-around">
            <VersionHistoryBlock versions={versionsIOS} />
            <VersionHistoryBlock versions={versionsAndroid} />
          </Box>
        </Box>
      </>
    );
  }

  return (
    <Container>
      <Header>Версiї</Header>

      <Box mt={"40px"} mb={"40px"}>
        <NavBar items={navItems} />
      </Box>

      <Paper elevation={4}>
        <ThemeProvider theme={defaultMaterialTheme}>{content}</ThemeProvider>
      </Paper>

      <ErrorMessageSnackbar
        open={requestError}
        closeHandler={() => setRequestError(false)}
        message={errorMessage}
      />
    </Container>
  );
};
